import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Checkbox, Pill, Title, Tooltip } from '@xsolla/ui-core';

import { UserAttribute, UserAttributes } from '../../../api/users/attributes/types';
import { ISortStatuses } from '../../../hooks/types';
import { Table } from '../../ui';
import ChangeValue from './ChangeValue';
import {
  AttributeKeyWrapper,
  CheckboxWrapper,
  EmptyTableWrapper,
  SubHeader3Wrapper,
  TableWrapper,
  TooltipWrapper
} from './styles';

type Props = {
  userId: string;
  tableData: UserAttributes;
  sortStatuses: ISortStatuses;
  currFilterData: UserAttributes | null;
  sortFunction: (key: string) => void;
  setData: React.Dispatch<React.SetStateAction<UserAttributes>>;
};

export const AttributesTable = ({
  userId,
  tableData,
  sortStatuses,
  setData,
  currFilterData,
  sortFunction
}: Props): JSX.Element => {
  const intl = useIntl();

  const renderTooltip = (label: string) => {
    return (
      <TooltipWrapper>
        <Tooltip
          content={intl.formatMessage({ id: label }, { b: (chunk: string) => <b>{chunk}</b> })}
        />
      </TooltipWrapper>
    );
  };

  const tableHeadProps = [
    { width: 3, separator: false },
    {
      width: 20,
      label: intl.messages['users.attributes.table.description.title'],
      name: 'description',
      tooltip: renderTooltip('users.attributes.table.description.tooltip'),
      sortFunc: sortFunction
    },
    {
      width: 15,
      label: intl.messages['users.attributes.table.key.title'],
      name: 'key',
      tooltip: renderTooltip('users.attributes.table.key.tooltip'),
      sortFunc: sortFunction
    },
    {
      width: 20,
      label: intl.messages['users.attributes.table.value.title'],
      tooltip: renderTooltip('users.attributes.table.value.tooltip'),
      name: 'value'
    },
    {
      width: 11,
      label: intl.messages['users.attributes.table.type.title'],
      name: 'data_type',
      sortFunc: sortFunction
    },
    {
      width: 11,
      label: intl.messages['users.attributes.table.access.title'],
      name: 'attr_type',
      tooltip: renderTooltip('users.attributes.table.access.tooltip'),
      sortFunc: sortFunction
    },
    {
      width: 11,
      label: intl.messages['users.attributes.table.publisher-project-id.title'],
      name: 'publisher_project_id',
      separator: true,
      tooltip: renderTooltip('users.attributes.table.publisher-project-id.description'),
      sortFunc: sortFunction
    },
    {
      width: 11,
      label: intl.messages['users.attributes.table.readonly.title'],
      tooltip: renderTooltip('users.attributes.table.readonly.tooltip'),
      name: 'readonly'
    },
    {
      width: 13,
      label: intl.messages['users.attributes.table.permission.title'],
      name: 'permission',
      separator: false,
      tooltip: renderTooltip('users.attributes.table.permission.tooltip'),
      sortFunc: sortFunction
    }
  ];

  const rowComponents = (attribute: UserAttribute) => ({
    main: [
      <div />,
      <SubHeader3Wrapper>{attribute.description}</SubHeader3Wrapper>,
      <AttributeKeyWrapper>{attribute.key}</AttributeKeyWrapper>,
      <ChangeValue userId={userId} attribute={attribute} setData={setData} />,
      <SubHeader3Wrapper>{attribute.data_type}</SubHeader3Wrapper>,
      <SubHeader3Wrapper>{attribute.attr_type}</SubHeader3Wrapper>,
      <SubHeader3Wrapper>{attribute.publisher_project_id}</SubHeader3Wrapper>,
      <CheckboxWrapper>{attribute.readonly && <Checkbox disabled checked />}</CheckboxWrapper>,
      <div>
        <Pill palette={attribute.permission === 'private' ? 'brand' : 'action'}>
          {intl.formatMessage({ id: `users.attributes.table.access.${attribute.permission}` })}
        </Pill>
      </div>
    ]
  });

  const noDataContent = useMemo(() => {
    return (
      <EmptyTableWrapper>
        <Title>{intl.messages['users.attributes.table.empty.title']}</Title>
        <p>{intl.messages['users.attributes.table.empty.subtitle']}</p>
      </EmptyTableWrapper>
    );
  }, []);

  const noSearchDataContent = useMemo(() => {
    return (
      <EmptyTableWrapper>
        <Title>{intl.messages['users.attributes.table.search.empty.title']}</Title>
        <p>{intl.messages['users.attributes.table.search.empty.subtitle']}</p>
      </EmptyTableWrapper>
    );
  }, []);

  return (
    <TableWrapper>
      <Table
        noDataContent={currFilterData ? noSearchDataContent : noDataContent}
        head={tableHeadProps}
        sortStatuses={sortStatuses}
        renderRow={rowComponents}
        tableData={currFilterData || tableData}
        isScrollable
      />
    </TableWrapper>
  );
};
